import {
  Stack,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronDown, ChevronLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { CardsBox } from "./Category.styles";
import {
  CardFilter,
  FiltersCarousel,
  GridMain,
  Spinner
} from "../../components";
import { useAnnouncement } from "../../components/Announcement/useAnnouncement";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CarouselCardRestyle } from "../../components/CarouselCardRestyle/CarouselCardRestyle.component";
import {
  STATUS_LO,
  PAGE_NAME
} from "../../consts";
import {
  useInfiniteContentQuery,
  useStructuresQuery,
  useTopicsQuery
} from "../../queries";
import { useAuthStore, useLanguageStore, useNavbarStore } from "../../stores";
import type {
  LearningObject,
  MetaData
} from "../../types";
import { createEndDate, createStartDate } from "../../utils/general";
import { FilterDrawer } from "../Explore/FilterDrawer.component";
type AllFilters = {
  typeFilter: {SYNC?: boolean, ASYNC?: boolean, BLENDED?: boolean}|undefined,
  statusFilter: {
    E?: boolean,
    P?: boolean,
    C?: boolean,
    W?:boolean,
    T?:boolean,
    PA?:boolean,
    PW?: boolean,
    R?:boolean,
    U?: boolean,
    F?: boolean,
    PA0?: boolean,
    N?: boolean
   }|undefined,
  capabilitiesFilter: {[key:number]:boolean}|undefined,
  knowledgeAreaFilter: {[key:number]:boolean}|undefined,
  knowledgeDomainFilter: {[key:number]:boolean}|undefined,
  campaignsFilter: {[key:number]:boolean}|undefined,
  levelsFilter: {[key:number]:boolean}|undefined,
  perTeFilter: {HR_SUGGESTED?:boolean,MANAGER_SUGGESTED?:boolean,AI_SUGGESTED?:boolean}|undefined
};


export function Category() {
  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  const { t  } = useTranslation();
  const { announce } = useAnnouncement();
  const navbarItem = useNavbarStore(state => state.item);
  const theme = useTheme();
  const { category } = useParams();
  const { state:routerState } = useLocation();
  const language = useLanguageStore(state => state.language)?.id;
  const setSpinnerLoginVisible = useAuthStore(state => state.setSpinnerLoginVisible);
  setSpinnerLoginVisible(false);


  const { data: pageStructure } = useStructuresQuery({
    pageName: navbarItem === "AGENDA" ? PAGE_NAME.AGENDA_OVERVIEW : PAGE_NAME.EXPLORE_OVERVIEW
  });
  const relativePath = routerState.relativePath ?? pageStructure?.relativePaths.find((relativePath) =>
    relativePath.title === decodeURI(category ?? "") || (`${relativePath.id}` === decodeURI(category ?? ""))
  );
  const type = useMemo(() => {
    if(relativePath?.serviceType !== "visCarCategory") {
      return relativePath?.serviceType?[relativePath?.serviceType]: undefined;
    }

    if (relativePath?.isAttribute) return ["attribute"];

    const regexpResult = /\/learning-catalogue\/[0-9]+\/[0-9]+\/categories\/(?<category>[0-9]+)\?.*/
      .exec(relativePath.apiPath);
    const category = regexpResult?.groups?.["category"];
    return category?[`visCarCategory${category}`]: undefined;
  }, [relativePath]);

  const [filtersQuery, setFiltersQuery] = useState("");
  const { data: allTopics } = useTopicsQuery({
    available: true,
    type,
    filterQuery: filtersQuery,
    relativePath
  });
  const [selectedTopics, setSelectedTopics] = useState<number[]>([]);

  const topicsQueryString = selectedTopics.reduce(
    /* the uri arrives with ..topics= */
    (currentTopicsQuery, currentTopic, currentTopicIndex) => {
      if (currentTopicIndex == 0 && currentTopic) {
        return currentTopicsQuery + currentTopic;
      } else {
        return currentTopicsQuery + "&topics=" + currentTopic;
      }
    },
    ""
  ) + (filtersQuery?`&${filtersQuery}`:"");
  useEffect(() => {
    if(routerState && routerState.topicsQueryString) {
      const availableTopicsIds = allTopics?.filter((topic) => topic.learning_object_associated > 0).map((topic) => topic.id) ?? [];
      const queryString = routerState.topicsQueryString;
      const params = new URLSearchParams(queryString);
      const topics = params.getAll("topics").map(Number);
      const firstPart = queryString.split("&")[0];
      if (!firstPart.includes("=") && !isNaN(firstPart)) topics.unshift(Number(firstPart));
      setSelectedTopics(topics.filter((topicId: number)=> availableTopicsIds.includes(topicId)));
    }
  }, [routerState?.topicsQueryString, allTopics]);

  // solo l'api attribute-learning-objects vuole come query param i filtri in learning_object_type
  let topicQueryStringChecked = topicsQueryString;
  if (!relativePath?.apiPath?.includes("attribute-learning-objects")) {
    topicQueryStringChecked = topicQueryStringChecked.replace(/course_path_type/g, "learning_object_type");
  }

  const {
    data: carouselData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading
  } = useInfiniteContentQuery<
    Record<
      string,
      LearningObject[]
    > & {
      metadata: MetaData
    }
  >({
    enabled: Boolean(relativePath),
    pageSize: 12,
    path: relativePath?.apiPath ?? "",
    //additional params
    topics: topicQueryStringChecked,
    language
  });
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [initialDrawerFilters, setInitialDrawerFilters] = useState<AllFilters | undefined>(undefined);

  useEffect(() => {
    const filtersFromStorage = sessionStorage.getItem("explore_filters");
    let allFilters : AllFilters = {
      typeFilter: undefined,
      statusFilter: undefined,
      capabilitiesFilter: undefined,
      knowledgeAreaFilter: undefined,
      knowledgeDomainFilter: undefined,
      campaignsFilter: undefined,
      levelsFilter: undefined,
      perTeFilter: undefined
    };
    if(filtersFromStorage != null) {
      try {
        allFilters = JSON.parse(filtersFromStorage);
        setInitialDrawerFilters(allFilters);
      } catch(err) {
        allFilters = {
          typeFilter: undefined,
          statusFilter: undefined,
          capabilitiesFilter: undefined,
          knowledgeAreaFilter: undefined,
          knowledgeDomainFilter: undefined,
          campaignsFilter: undefined,
          levelsFilter: undefined,
          perTeFilter: undefined
        };
      }
    }
    if(Object.values(allFilters).find(item=>!!item)) {
      applyFiltersCallback(
        allFilters.typeFilter,
        allFilters.statusFilter,
        allFilters.capabilitiesFilter,
        allFilters.knowledgeAreaFilter,
        allFilters.knowledgeDomainFilter,
        allFilters.campaignsFilter,
        allFilters.levelsFilter
      );
    }
    setFiltersLoaded(true);
  }, [filtersLoaded]);
  const applyFiltersCallback = (typeFilter: {SYNC?: boolean, ASYNC?: boolean, BLENDED?: boolean}|undefined,
    statusFilter: {
    E?: boolean,
    P?: boolean,
    C?: boolean,
    W?:boolean,
    T?:boolean,
    PA?:boolean,
    PW?: boolean,
    R?:boolean,
    U?: boolean,
    F?: boolean,
    PA0?: boolean,
    N?: boolean
   }|undefined,
    capabilitiesFilter: {[key:number]:boolean}|undefined,
    knowledgeAreaFilter: {[key:number]:boolean}|undefined,
    knowledgeDomainFilter: {[key:number]:boolean}|undefined,
    campaignsFilter: {[key:number]:boolean}|undefined,
    levelsFilter: {[key:number]:boolean}|undefined) => {
    let query : string[][] = [];
    if(typeFilter) {
      query.push(...Object.entries(typeFilter)
        .filter((entry) => entry[1]).map(entry=>["learning_object_type", entry[0]]));
    }
    if(statusFilter) {
      query.push(...Object.entries(statusFilter)
        .filter(entry => entry[1]).map(entry=>["enroll_status",entry[0]]));
    }
    if(capabilitiesFilter) {
      query.push(...Object.entries(capabilitiesFilter)
        .filter(entry => entry[1]).map(entry=>["autotagging_capabilities",entry[0]]));
    }
    if(knowledgeAreaFilter) {
      query.push(...Object.entries(knowledgeAreaFilter)
        .filter(entry => entry[1]).map(entry=>["autotagging_knowledge_areas",entry[0]]));
    }
    if(knowledgeDomainFilter) {
      query.push(...Object.entries(knowledgeDomainFilter)
        .filter(entry => entry[1]).map(entry=>["autotagging_knowledge_domains", entry[0]]));
    }
    if(campaignsFilter) {
      query.push(...Object.entries(campaignsFilter)
        .filter(entry => entry[1]).map(entry=>["campaigns", entry[0]]));
    }
    if(levelsFilter) {
      query.push(...Object.entries(levelsFilter)
        .filter(entry => entry[1]).map(entry=>["autotagging_levels", entry[0]]));
    }
    setFiltersQuery(new URLSearchParams(query).toString());
  };
  const responseKey = carouselData
    ? (Object.keys(carouselData?.pages?.[0]).find((key) => key !== "metadata") ?? "")
    : "";
  const carouselCards = (carouselData && responseKey)
    ? (
      carouselData?.pages?.reduce((acc, page) => {
        const contents = page[responseKey] ?? [];
        return acc.concat(contents);
      }, [] as LearningObject[])
    ) : [];
  const title = routerState?.titleCards ? routerState.titleCards :
    (relativePath?.serviceType && relativePath?.serviceType !== "visCarCategory") ?
      t(relativePath?.serviceType) : relativePath?.title;

  useEffect(() => {
    if(title){
      announce( `${t("announcement_explore_category")} ${title}`);
    }
  }, [title]);
  
  return (
    <GridMain>
      <Stack
        maxWidth="calc(100vw - (100vw - 100%))"
        minWidth={ "100%" }
        sx={ {
          ".button-back": {
            color: theme.customColors.textPrimary,
            fontSize: "1.141rem",
            fontWeight: "400",
            gap: "9px",
            justifyContent: "flex-start",
            padding: "32px 16px 37px",
            textAlign: "start",

            [theme.breakpoints.down("sm")]: {
              paddingLeft: "0"
            },

            "&: hover":{
              background: "transparent"
            },

            ".icon":{
              height: "24px",
              width: "auto"
            }
          }
        } }
      >
        <ButtonDigited
          typology="tertiary"
          aria-label={ t("go_back", "Indietro") }
          href="/esplora"
          onClick={ () => setNavbarItem("EXPLORE") }
          buttonSize="sm"
          sx={ { textDecoration: "none" } }
          startIcon={ <ChevronLeft
            color={ theme.customColors.systemPrimary01 }
            size={ "1rem" }
          /> }
          SxText={ {
            fontSize: "0.75rem"
          } }
          text={ t("go_back", "Indietro") }
        />
        <Typography
          variant={ "h4" }
          fontWeight={ "700" }
          sx={ {
            marginBottom: "30px",
            paddingLeft: "0",
            paddingRight: "0",
            [theme.breakpoints.down("sm")]: {
              fontSize: "1.5rem",
              marginBottom: "16px"
            }
          } }
        >
          { title }
        </Typography>
        <FilterDrawer
          onChange={ (q) => setFiltersQuery(q.toString()) }
          nameKeyFilterStorage="explore_filters"
          initialFilters={ initialDrawerFilters }
          readOnly={ true }></FilterDrawer>
        <FiltersCarousel
          label={ "filters-carousel" }
          hasPagination={ false }
        >
          {
            allTopics?.map((topic) => (
              topic.learning_object_associated > 0 ? (
                <CardFilter
                  key={ `topic--${topic.id}` }
                  coverPublicURL={ topic.url.public_url }
                  onClickhandler={ () => {
                    const currentSelectedCategories = [...selectedTopics];
                    if (selectedTopics.includes(topic.id)) {
                      currentSelectedCategories.splice(
                        currentSelectedCategories.indexOf(topic.id),
                        1
                      );
                    } else {
                      currentSelectedCategories.push(topic.id);
                      currentSelectedCategories.sort();
                    }
                    setSelectedTopics(currentSelectedCategories);
                  } }
                  selected={ selectedTopics.includes(topic.id) }
                  title={ topic.name }
                />
              ) : null
            ))
          }
        </FiltersCarousel>

        <CardsBox $isLoading={ isLoading } >
          {
            isLoading && !!relativePath ? (
              <Spinner />
            ) : (
              carouselCards?.map((card) => (
                <CarouselCardRestyle
                  key={ `carousel-category-detail--card--${card.id}--${language}` }
                  loTag={ card.lo_Tag }
                  isLinkRelative={ card.isLinkRelative }
                  link={ card.link }
                  canAccessNow={ card.canAccessNow }
                  enrollType={ card.enrollType }
                  booked={ card.status !== STATUS_LO.N && card.status !== STATUS_LO.T }
                  categories={ card.topicTags || [] }
                  coverPublicURL={ card?.coverPublicURL?.toString() || "" }
                  coverVideoPublicURL={ card.videoPublicURL }
                  disabled={ false }
                  duration={ card.duration }
                  ecm_specialization={ card.ecm_specialization }
                  ecmRegistration={ card.ecmRegistration }
                  ecmType={ card.ecmType }
                  editionNumber={ card.editionNumber }
                  endDate={ createEndDate(card) }
                  expirationDate={ card.expirationDate }
                  id={ card.id }
                  isAutoSubscribeEnable={ card.isAutoSubscribeEnable }
                  isFavourite={ card.isFavourite }
                  isMandatory={ card.isMandatory }
                  isSurvey={ card.isSurvey }
                  isEditorial={ card.isEditorial }
                  isTest={ card.isTest }
                  isToj={ card.isToj }
                  learningObjectType={ card.learningObjectType }
                  learningObjectTypology={ card.learningObjectTypology }
                  percentageOfCompletion={ card.percentageOfCompletion }
                  shortDescription={ card.shortDescription }
                  startDate={ createStartDate(card) }
                  status={ card.status || "N" }
                  title={ card.title }
                  cardType={ "small" }
                  externalFileUrl={ card?.externalFileUrl }
                  isCarouselView={ false }
                />
              ))
            )
          }

        </CardsBox>
        {
          (hasNextPage && !isFetchingNextPage) ? (
            <ButtonDigited
              typology="primary"
              disabled={ !hasNextPage }
              onClick={ () => fetchNextPage() }
              sx={ {
                margin:"0 auto",
                marginBottom:"1.5rem"
              } }
            >
              <>
                { t("load_more") }
                <ChevronDown
                  size="1rem"
                />
              </>
            </ButtonDigited>
          ) : null
        }
        {
          isFetchingNextPage ? (
            <Spinner />
          ) : null
        }
      </Stack>
    </GridMain>
  );
}
